// Content pulled directly from The Catalyst Continuum published portal and
// the source Perplexity thread. Kept as structured data so we can render it
// in dashboard-style cards without hardcoding markup.

export const FRAMEWORKS = [
  {
    id: "asam",
    name: "ASAM Criteria 4th Ed.",
    origin: "American Society of Addiction Medicine, 2023",
    job: "Determines the right level of care and when to move up or down.",
    where: "Admission, weekly utilization review, discharge.",
  },
  {
    id: "narr",
    name: "NARR Standards",
    origin: "National Alliance for Recovery Residences",
    job: "Defines the four levels of recovery housing and the operational, ethical, and staffing standards for each.",
    where: "Sober living / recovery housing operations.",
  },
  {
    id: "simor",
    name: "SIMOR",
    origin: "Best et al., 2016 — Social Identity Model of Recovery",
    job: "Reframes recovery as a social-identity transition from using-group to recovery-group.",
    where: "Peer support, group therapy, alumni programming, aftercare.",
  },
  {
    id: "4e",
    name: "4E Cognition",
    origin: "Newen, De Bruin, Gallagher, 2018 (embodied / embedded / enacted / extended)",
    job: "Treats mind as a brain-body-environment system, so we intervene in the body and the environment, not only in cognition.",
    where: "Somatic experiencing, milieu design, environmental cues, tools/journaling as extended mind.",
  },
  {
    id: "chime",
    name: "CHIME",
    origin: "Leamy et al., 2011",
    job: "Names the five recovery processes (Connectedness, Hope, Identity, Meaning, Empowerment) that every intervention should reinforce.",
    where: "Treatment planning, group curriculum, discharge readiness.",
  },
  {
    id: "rosc",
    name: "ROSC",
    origin: "SAMHSA, 2005–present",
    job: "Organizes services and community supports around the patient's chosen recovery pathway, across levels of care.",
    where: "Care coordination, community partnerships, alumni, recovery capital.",
  },
  {
    id: "fit",
    name: "Feedback-Informed Treatment (FIT)",
    origin: "Miller, Duncan, Bargmann (ICCE)",
    job: "Uses ultra-brief outcome (ORS) and alliance (SRS) measures every session to prevent deterioration and dropout.",
    where: "Every therapy contact; supervision.",
  },
  {
    id: "mbc",
    name: "Measurement-Based Care (MBC)",
    origin: "Fortney, Scott, Trivedi et al.",
    job: "Uses standardized symptom measures (PHQ-9, GAD-7, PCL-5, AUDIT, DAST, WHODAS) to guide treatment decisions.",
    where: "Admission, weekly, at every level-of-care change.",
  },
  {
    id: "dp",
    name: "Deliberate Practice",
    origin: "Ericsson; Rousmaniere; Miller",
    job: "A structured method of solo and supervised practice targeting specific weak-signal skills.",
    where: "Clinical supervision, BHT coaching, executive coaching.",
  },
];

export const LEVELS_OF_CARE = [
  {
    id: "housing",
    segment: "Recovery Housing",
    levels: "NARR I–IV",
    setting: "Peer-run to clinically supported homes",
    los: "3–18 months",
    mission: "Environmental stability, social-identity change (SIMOR), recovery capital.",
  },
  {
    id: "op",
    segment: "Outpatient",
    levels: "1.0, 1.5, 1.7",
    setting: "Office, telehealth",
    los: "3–12 months",
    mission: "Manage symptoms and cravings while patient lives at home and works.",
  },
  {
    id: "iop",
    segment: "Intensive Outpatient / PHP",
    levels: "2.1, 2.5, 2.7",
    setting: "Day-treatment facility",
    los: "4–12 weeks",
    mission: "Structured hours; step-down from residential or step-up from OP.",
  },
  {
    id: "res",
    segment: "Residential",
    levels: "3.1, 3.3, 3.5",
    setting: "24-hour non-medical residential",
    los: "30–180 days",
    mission: "Stabilize and integrate; SIMOR-based milieu; CHIME-oriented curriculum.",
  },
  {
    id: "mm",
    segment: "Medically Monitored / Withdrawal Mgmt",
    levels: "3.7 (incl. 3.7-WM)",
    setting: "Nurse-staffed, physician-directed",
    los: "3–21 days",
    mission: "Medical monitoring during acute withdrawal or high-risk stabilization.",
  },
  {
    id: "inpatient",
    segment: "Medically Managed Inpatient",
    levels: "4.0 (incl. 4.0-WM)",
    setting: "Hospital-level",
    los: "3–14 days",
    mission: "24-hour physician / RN care; life-threatening or unstable co-occurring.",
  },
];

export const ROLES = [
  {
    id: "reception",
    name: "Receptionist / Access Coordinator",
    primary: ["ROSC", "ASAM Dimensions (recognition)"],
    secondary: ["CHIME (Hope)"],
    purpose: "First human contact — determines whether the person calls back tomorrow.",
  },
  {
    id: "admin",
    name: "Administrator / Program Director",
    primary: ["ASAM (LOC integrity)", "ROSC"],
    secondary: ["MBC dashboard", "Deliberate Practice"],
    purpose: "Runs the operation so clinicians can deliver care.",
  },
  {
    id: "exec",
    name: "Executive (CEO / CCO / COO)",
    primary: ["ROSC", "Deliberate Practice"],
    secondary: ["MBC (outcomes)", "CHIME (culture)"],
    purpose: "Builds and defends the system that makes the frameworks possible.",
  },
  {
    id: "bht",
    name: "Behavioral Health Technician (BHT)",
    primary: ["4E (milieu, embodiment)", "SIMOR (group dynamics)"],
    secondary: ["CHIME", "ASAM (observation)"],
    purpose: "Runs the milieu — where 80% of the healing minutes happen.",
  },
  {
    id: "cm",
    name: "Case Manager",
    primary: ["ROSC", "SIMOR"],
    secondary: ["ASAM Dim. 5–6", "CHIME"],
    purpose: "Builds the recovery capital that outlives the treatment episode.",
  },
  {
    id: "therapist",
    name: "Therapist / Counselor",
    primary: ["FIT", "MBC", "Deliberate Practice"],
    secondary: ["CHIME", "4E", "SIMOR"],
    purpose: "Delivers the clinical relationship as the primary intervention.",
  },
  {
    id: "nurse",
    name: "Nursing",
    primary: ["ASAM Dim. 1–2", "MBC (medical)"],
    secondary: ["4E (somatic awareness)", "CHIME (Hope)"],
    purpose: "Keeps patients medically safe; makes withdrawal a therapeutic event.",
  },
  {
    id: "md",
    name: "Medical (MD / DO / PMHNP / PA)",
    primary: ["ASAM Dim. 1–3", "MBC"],
    secondary: ["ROSC", "Deliberate Practice"],
    purpose: "Provides diagnosis, medication, and medical direction.",
  },
];

export const ASAM_DIMENSIONS = [
  {
    n: 1,
    title: "Intoxication, Withdrawal, and Addiction Medications",
    detail:
      "Are they in withdrawal now, at risk of withdrawal, or in need of medication for addiction? Nurses and medical providers own scoring; BHTs report observations.",
  },
  {
    n: 2,
    title: "Biomedical Conditions",
    detail:
      "Physical health that affects treatment — pregnancy, hepatitis, chronic pain, diabetes. Nursing owns scoring; case managers own coordination.",
  },
  {
    n: 3,
    title: "Psychiatric, Cognitive, and Emotional Conditions",
    detail: "Co-occurring conditions and current risk. Therapists and prescribers own scoring; BHTs escalate warning signs.",
  },
  {
    n: 4,
    title: "Substance Use / Addictive Behavior — Related Risks",
    detail:
      "Consolidates readiness-to-change into service planning; also captures behavioral risks (gambling, sex, food) when clinically relevant.",
  },
  {
    n: 5,
    title: "Recovery Environment — Interpersonal",
    detail: "Family, peers, social network — the SIMOR-relevant dimension. Case managers and therapists own this jointly.",
  },
  {
    n: 6,
    title: "Recovery Environment — Community, Cultural, Social, Structural",
    detail: "Housing, employment, legal, transportation, culture, discrimination. Case managers own this.",
  },
];

export const NON_NEGOTIABLES = [
  "ORS and SRS every session — feedback-informed treatment is the floor, not the ceiling.",
  "MBC on admission, weekly, and at every LOC transition (PHQ-9, GAD-7, one substance measure).",
  "Person-centered treatment plans in the patient's own language, mapped to CHIME domains.",
  "Warm handoffs at every LOC transition — a phone or video call between sending and receiving clinicians.",
  "Recovery capital is measured with the ARC or BARC-10 at admission, discharge, and 90 days post.",
  "Alumni engagement is a service line, not a marketing tactic.",
  "Deliberate practice for every clinician, every month — one skill, one recording, one tweak.",
  "Every staff member trained in trauma-informed care and Motivational Interviewing basics.",
  "Documentation is a clinical act — notes are written to move the patient forward.",
  "Nothing is delivered without a cultural humility and health-equity check.",
];

export const PRICING_TIERS = [
  {
    id: "self-serve",
    tier: "Tier 1",
    name: "Self-Serve License",
    price: "$1,495 – $2,995",
    cadence: "/year",
    featured: false,
    bestFor: "Small residential/OP operators, startup SUD facilities, NARR homes going for Level III/IV.",
    included: [
      "Portal access (single org, up to 25 users)",
      "All PDFs, editable Word bundle, forms, quick cards",
      "12-month license, annual renewal at 60% for updates",
      "No support, no customization",
    ],
    scale: {
      driver: "Organization size and seat count",
      low: "Single site, small operator, up to 10 users — $1,495",
      high: "Up to 25 users, single org, one license — $2,995",
      note: "Above 25 users or multiple sites — add-on at $500–$1,000/site/yr.",
    },
  },
  {
    id: "guided",
    tier: "Tier 2",
    name: "Guided Implementation",
    price: "$12,500 – $25,000",
    cadence: "one-time · 90 days",
    featured: true,
    bestFor: "Mid-size behavioral health orgs (1–3 sites, 25–100 staff), new programs launching, orgs prepping for JCAHO/CARF.",
    included: [
      "Everything in Tier 1",
      "6–8 sessions with James (kickoff, role rollouts, QA rubric calibration, mock survey)",
      "Light customization of the QA dashboard to their EHR/workflow",
      "90-day Slack/email access",
    ],
    scale: {
      driver: "Scope complexity and accreditation stakes",
      low: "One site, standard operator, no active survey window — $12,500",
      high: "2–3 sites, active JCAHO/CARF prep, custom rubric build, 8 sessions — $25,000",
      note: "Bottom of range = foundation work. Top = prepping for a specific survey with a hard deadline.",
    },
  },
  {
    id: "turnkey",
    tier: "Tier 3",
    name: "Turnkey Deployment",
    price: "$45,000 – $95,000",
    cadence: "one-time · 6 months",
    featured: false,
    bestFor: "Multi-site operators, PE-backed BH platforms, hospital systems adding a BH service line.",
    included: [
      "Everything in Tier 2",
      "On-site training days (2–4 days)",
      "Custom co-branding of all documents",
      "Fidelity monitoring for 6 months",
      "Board/executive briefing deck",
    ],
    scale: {
      driver: "Site count and stakeholder complexity",
      low: "2–4 sites, single leadership team, one training week — $45,000",
      high: "5+ sites or hospital-embedded service line, multi-region rollout — $95,000",
      note: "This tier scales with operational surface area.",
    },
  },
  {
    id: "cco",
    tier: "Tier 4",
    name: "Fractional CCO / Retained Advisory",
    price: "$8,000 – $18,000",
    cadence: "/month · 6-mo min",
    featured: false,
    bestFor: "Operators who need clinical leadership but cannot hire a full-time CCO yet.",
    included: [
      "Ongoing implementation partner",
      "Monthly QA review, quarterly board reporting",
      "Ad-hoc clinical operations counsel",
      "Fractional CCO responsibilities",
    ],
    scale: {
      driver: "Time commitment and scope of authority",
      low: "~8 hours/week, QA review + monthly board reporting, no personnel authority — $8,000/mo",
      high: "~20 hours/week, full CCO responsibilities, direct line to CEO — $18,000/mo",
      note: "Bottom = advisor on retainer. Top = fractional CCO.",
    },
  },
];

export const PRICING_ADDONS = [
  { name: "Additional site license", price: "$500–$1,000/site/year" },
  { name: "Custom framework addendum (MAT, adolescent, etc.)", price: "$3,500–$7,500" },
  { name: "Annual update subscription (post-Year 1)", price: "$895/year" },
  { name: "Live 90-min framework training for staff cohorts", price: "$1,250/session" },
];

export const PRICING_FAQ = [
  {
    q: "Can we start with the license and upgrade later?",
    a: "Yes. Every dollar spent on Tier 1 in the first 90 days is credited toward a Tier 2 upgrade.",
  },
  {
    q: "Do you sign a BAA?",
    a: "Yes. All client engagements include a Business Associate Agreement.",
  },
  {
    q: "Can we white-label the playbook?",
    a: "Co-branding is included at Tier 3. Full white-label is a separate quote.",
  },
  {
    q: "How is the range priced in practice?",
    a: "We quote the midpoint in discovery. To reduce price, we subtract scope — not negotiate the number.",
  },
];

export const BUYER_TYPES = [
  {
    name: "New / scaling BH operator",
    signals: "Just licensed, 30–90 days from opening or first survey",
    keeps_up: "Getting past the first JCAHO/CARF survey without staff meltdown",
    tier: "Tier 2",
    rollout: "Foundation + role playbooks + mock survey",
  },
  {
    name: "CEO / CCO / Medical Director",
    signals: "Existing operator with fragmented care levels and no shared operating system",
    keeps_up: "Turnover, documentation gaps, payer audits",
    tier: "Tier 2 or 4",
    rollout: "Guided rollout, or fractional CCO on retainer",
  },
  {
    name: "PE-backed BH platform",
    signals: "Multi-site roll-up, needs clinical infrastructure fast",
    keeps_up: "Standardizing care across acquired facilities",
    tier: "Tier 3",
    rollout: "Turnkey, custom co-brand, multi-region training",
  },
  {
    name: "Hospital adding BH service line",
    signals: "New inpatient unit or IOP embedded in hospital system",
    keeps_up: "Integrating BH into medical culture and documentation",
    tier: "Tier 3",
    rollout: "Turnkey with medical-integration emphasis",
  },
  {
    name: "State agency / Medicaid MCO",
    signals: "Provider network improvement or audit-readiness partner",
    keeps_up: "Provider variability, outcomes reporting",
    tier: "Custom",
    rollout: "Network-wide licensing + training cohorts",
  },
  {
    name: "Individual clinician / small NARR home",
    signals: "One or two clinicians, small recovery residence",
    keeps_up: "Documentation and NARR Level III/IV certification",
    tier: "Tier 1",
    rollout: "Self-serve license + optional single training session",
  },
];

export const PLAYBOOK_SYSTEM_PROMPT = `You are the Catalyst Continuum assistant. You answer questions grounded in this behavioral-health continuum-of-care playbook developed by Catalyst Behavioral Health Consulting, LLC (author: James M., LPC).

Voice rules:
- Person-first, operational, restrained. Never use emojis or exclamation points.
- If a question is outside the playbook (e.g., financial advice, unrelated medicine), politely say so and redirect to what the playbook does cover.
- When citing frameworks, use their proper name (ASAM 4th Ed., NARR, SIMOR, 4E, CHIME, ROSC, FIT, MBC, Deliberate Practice).
- Prefer concrete guidance to abstract theory. Use short sections with headers when helpful.

Playbook context you can draw on:

## The Framework Stack
- ASAM Criteria 4th Ed. (2023) — determines level of care and transitions. Used at admission, weekly utilization review, discharge.
- NARR Standards — defines the four levels of recovery housing (I peer-run through IV clinically supported).
- SIMOR — Social Identity Model of Recovery, reframes recovery as identity transition.
- 4E Cognition — embodied/embedded/enacted/extended; mind as brain-body-environment system.
- CHIME — Connectedness, Hope, Identity, Meaning, Empowerment.
- ROSC — Recovery-Oriented System of Care; organizes services around patient's pathway.
- FIT — Feedback-Informed Treatment; ORS/SRS every session.
- MBC — Measurement-Based Care; PHQ-9, GAD-7, PCL-5, AUDIT, DAST, WHODAS.
- Deliberate Practice — Ericsson/Rousmaniere/Miller structured skill development.

## Levels of Care
- Recovery Housing (NARR I–IV): peer-run to clinically supported, 3–18 months.
- Outpatient 1.0/1.5/1.7: office/telehealth, 3–12 months.
- IOP/PHP 2.1/2.5/2.7: day-treatment, 4–12 weeks.
- Residential 3.1/3.3/3.5: 24-hour non-medical, 30–180 days.
- Medically Monitored 3.7 (incl. 3.7-WM): nurse-staffed, physician-directed, 3–21 days.
- Medically Managed Inpatient 4.0 (incl. 4.0-WM): hospital-level, 3–14 days.

## ASAM Dimensions (4th Edition)
Six dimensions used at every intake and transition:
1. Intoxication, Withdrawal, and Addiction Medications
2. Biomedical Conditions
3. Psychiatric, Cognitive, and Emotional Conditions
4. Substance Use / Addictive Behavior — Related Risks (readiness-to-change now a service-planning consideration)
5. Recovery Environment — Interpersonal
6. Recovery Environment — Community, Cultural, Social, Structural

## Roles & Framework Assignments
Each role has a "portfolio" of 2-4 frameworks:
- Receptionist: ROSC + ASAM recognition, secondary CHIME (Hope)
- Program Director: ASAM (LOC integrity) + ROSC, secondary MBC + Deliberate Practice
- Executive: ROSC + Deliberate Practice, secondary MBC + CHIME
- BHT: 4E + SIMOR, secondary CHIME + ASAM observation
- Case Manager: ROSC + SIMOR, secondary ASAM Dim 5-6 + CHIME
- Therapist: FIT + MBC + Deliberate Practice, secondary CHIME + 4E + SIMOR
- Nursing: ASAM Dim 1-2 + MBC medical, secondary 4E + CHIME (Hope)
- Medical: ASAM Dim 1-3 + MBC, secondary ROSC + Deliberate Practice

## Non-Negotiables
Always true regardless of level, role, or state:
1. ORS and SRS every session
2. MBC on admission, weekly, at every LOC transition
3. Person-centered plans in the patient's own language, CHIME-mapped
4. Warm handoffs at every transition
5. Recovery capital measured (ARC/BARC-10) at admission/discharge/90 days
6. Alumni engagement as a service line, not a marketing tactic
7. Monthly deliberate practice for every clinician
8. All staff trained in trauma-informed care and MI basics
9. Documentation is a clinical act
10. Cultural humility and health-equity check on every intervention

## Pricing
- Tier 1 Self-Serve License: $1,495–$2,995/yr — small operators, NARR homes
- Tier 2 Guided Implementation: $12,500–$25,000 one-time / 90 days — mid-size orgs, JCAHO/CARF prep [MOST POPULAR]
- Tier 3 Turnkey Deployment: $45,000–$95,000 one-time / 6 months — multi-site, PE-backed, hospital systems
- Tier 4 Fractional CCO: $8,000–$18,000/mo (6-month min) — orgs needing CCO leadership without full-time hire

When users ask factual playbook questions, answer directly. When they ask about pricing or fit, help them find the right tier. When they ask something outside the playbook, say so and offer to route them to a discovery call.`;
